import * as THREE from 'three';
import { shaderMaterial } from '@react-three/drei';
import { extend, ThreeElement } from '@react-three/fiber';

export interface IsogridUniforms {
  boxMin: THREE.Vector3;
  boxMax: THREE.Vector3;
  showPositionColors: number;
  lineThickness: number;
  lineDensity: number;
  opacity: number;
  time: number;
  theta: number;
}

export type IsogridMaterialType = THREE.ShaderMaterial & {
  cameraMatrixWorld?: THREE.Matrix4;
  cameraProjectionMatrixInv?: THREE.Matrix4;
  lightPlaneNormal?: THREE.Vector3;
  lightPlaneConstant?: number;
  normalTexture?: THREE.Texture | null;
  depthTexture?: THREE.Texture | null;
  lightDir?: THREE.Vector3;
  near?: number;
  far?: number;
  modelMatrix?: THREE.Matrix4;
  worldRadius?: number;
  ior?: number;
  bounces?: number;
  resolution?: number;
  size?: number;
  intensity?: number;
  uniforms: {
    time: { value: number };
    boxMin: { value: THREE.Vector3 };
    boxMax: { value: THREE.Vector3 };
    showPositionColors: { value: number };
    lineDensity: { value: number };
    lineThickness: { value: number };
    theta: { value: number };
    color: { value: THREE.Color };
  };
};

export const IsogridMaterial = /* @__PURE__ */ shaderMaterial(
  {
    boxMin: new THREE.Vector3(),
    boxMax: new THREE.Vector3(),
    showPositionColors: 0,
    lineDensity: 10,
    lineThickness: 1,
    opacity: 1,
    time: 0,
    theta: 0,
    color: new THREE.Color('#ffffff'),
  },

  `
  varying vec3 vPos;

  void main() {
    vec4 worldPos = modelMatrix * vec4(position, 1.0);
    vPos = worldPos.xyz;
    gl_Position = projectionMatrix * viewMatrix * worldPos;
  }
  `,
  // fragment shader
  `
  varying vec3 vPos;
uniform vec3 boxMin;
uniform vec3 boxMax;
uniform float lineThickness;
uniform float showPositionColors;
uniform float time;
uniform float opacity;
uniform float lineDensity; // lines per world-unit (frequency)
uniform float theta;
uniform vec3 color;

void main() {
  // Rotate XZ around box center
  vec2 center = 0.5 * (boxMin.xz + boxMax.xz);
  vec2 p = vPos.xz - center;
  float c = cos(theta), s = sin(theta);
  vec2 pr = vec2(c * p.x - s * p.y, s * p.x + c * p.y) + center;

  // Ensure density > 0
  float density = max(lineDensity, 1e-4);

  // <<< Key change >>> scale coords by density so it controls spacing
  // Higher density => more lines (tighter spacing)
  vec2 coord = pr * density + time * 0.5;

  // Anti-aliased grid; fwidth(coord) keeps line thickness stable in screen px
  vec2 grid = abs(fract(coord - 0.5) - 0.5) / fwidth(coord) / lineThickness;
  float line = min(grid.x, grid.y);

  float col = 1.0 - min(line, 1.0);
  col = pow(col, 1.0 / 2.2);
  gl_FragColor = vec4(color * vec3(col), col * opacity);
}
  `,
  (material) => {
    if (material) {
      // @ts-expect-error sets the derivatives extension
      material.extensions.derivatives = true;
    }
  },
);

declare module '@react-three/fiber' {
  interface ThreeElements {
    isogridMaterial: ThreeElement<typeof IsogridMaterial>;
  }
}

extend({ IsogridMaterial });
